"use client";

import React, { useContext, useMemo, useState } from "react";
import Link from "next/link";
import styles from "./HeaderCoupon.module.scss";
import { AppContext } from "../../context/AppContext";
import {
    COUPON_MAIN_DEFAULT,   // guest (home page, user nije ulogovan)
    COUPON_MAIN_NONDEP,    // non-depositor klasa
    COUPON_MAIN_OPTIMIST,  // Bronze Tail / Bronze Tail Silver
    COUPON_MAIN_ELITE,     // VIP Bronze Tail / VIP - Transferred
} from "../../data/couponSets";

import CouponInfoModal from "../couponInfoModal/CouponInfoModal";
import CouponErrorModal from "../couponErrorModal/CouponErrorModal";
import { ActivateCoupon } from "../../services/coupon";

type CouponConfig = {
    title: string;
    bonus: string;
    mco?: string;
    minDeposit?: string;
    wagering?: string;
    maxBet?: string;
    maxBonus?: string;
    games?: string;
    available?: string[];
    code?: string;
    infoText?: string;
    ctaHref: string;
    mainValue?: string;
};

type SetKey = "default" | "nondep" | "optimist" | "elite";

const MAIN_BY_SET: Record<SetKey, CouponConfig> = {
    default: COUPON_MAIN_DEFAULT,
    nondep: COUPON_MAIN_NONDEP,
    optimist: COUPON_MAIN_OPTIMIST,
    elite: COUPON_MAIN_ELITE,
};

function mapPlayerClass(rawClass?: string): SetKey {
    if (!rawClass) return "default";

    const normalized = rawClass.toLowerCase().trim();

    if (normalized.includes("non") && normalized.includes("dep")) {
        return "nondep";
    }
    if (normalized.includes("bronze tail") || normalized.includes("silver")) {
        return "optimist";
    }
    if (normalized.includes("vip")) {
        return "elite";
    }

    return "default";
}

type CouponErrState = {
    open: boolean;
    status: number | null;
    message: string;
};

// 100% Cashback on First Deposit

export default function HeaderCoupon() {
    const {
        user,
        setPageLoader,
        setActiveCoupon,
        setIsCouponActivated,
        setIsClosed,
    } = useContext(AppContext);

    const [busy, setBusy] = useState(false);
    const [couponErr, setCouponErr] = useState<CouponErrState>({
        open: false,
        status: null,
        message: "",
    });
    const [infoCoupon, setInfoCoupon] = useState<CouponConfig | null>(null);

    const coupon = useMemo(() => {
        if (user) return null;

        return MAIN_BY_SET.default;
    }, [user]);

    if (!coupon) return null;

    // === Redeem logika za "Claim bonus" ===
    async function handleClaimClick(
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) {
        // ako nema koda -> običan link
        if (!coupon.code) return;

        e.preventDefault();
        e.stopPropagation();

        if (!user) {
            // guest -> otvori auth modal
            setIsClosed?.(false);
            return;
        }

        if (busy) return;

        try {
            setBusy(true);
            setPageLoader?.(true);

            const resp = await ActivateCoupon({ cuponCode: coupon.code });

            if (resp !== 204 && Array.isArray(resp)) {
                const next = resp[0];
                setActiveCoupon?.(next);
                setIsCouponActivated?.(next?.bank_method_list ? false : true);

                setCouponErr({
                    open: true,
                    status: 200,
                    message: `Coupon ${coupon.code} successfully redeemed!`,
                });
            } else {
                setIsCouponActivated?.(true);
                setCouponErr({
                    open: true,
                    status: 200,
                    message: `Coupon ${coupon.code} activated.`,
                });
            }
        } catch (err: any) {
            const status = err?.response?.status ?? null;
            const msg =
                err?.response?.data?.message ||
                err.message ||
                "Redeem failed.";
            setCouponErr({ open: true, status, message: msg });
        } finally {
            setBusy(false);
            setPageLoader?.(false);
        }
    }

    return (
        <>
            <div className={styles.couponWrapper}>
                <div className={styles.badge}>Exclusive Offer</div>

                <div className={styles.headerRow}>
                    <p className={styles.title}>{coupon.title}</p>

                    {/* INFO dugme – isti pattern kao cardInfo na mobilnom */}
                    <button
                        type="button"
                        className={styles.cardInfo}
                        aria-label="More info"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setInfoCoupon(coupon);
                        }}
                    />
                </div>

                <p className={styles.bonus}>{coupon.mainValue || coupon.bonus}</p>

                {coupon.minDeposit && (
                    <p className={styles.meta}>
                        Min deposit: <strong>{coupon.minDeposit}</strong>
                    </p>
                )}

                {coupon.code && (
                    <p className={styles.code}>
                        Use code: <span>{coupon.code}</span>
                    </p>
                )}

                <Link
                    href={coupon.ctaHref || "/promotions"}
                    className={styles.cta}
                    onClick={handleClaimClick}
                >
                    {user
                        ? busy
                            ? "Applying…"
                            : "Claim bonus"
                        : "Claim bonus"}
                </Link>
            </div>

            {/* INFO MODAL */}
            <CouponInfoModal
                open={!!infoCoupon}
                onClose={() => setInfoCoupon(null)}
                coupon={infoCoupon}
            />

            {/* ERROR / SUCCESS MODAL */}
            <CouponErrorModal
                open={couponErr.open}
                onClose={() =>
                    setCouponErr({ open: false, status: null, message: "" })
                }
                statusCode={couponErr.status}
                genericMessage={couponErr.message}
            />
        </>
    );
}
